import { getData, postData } from './common.service';

const BASE = '/tickets';
const AUTH = {
  basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  sendTokenInHeader: true,
};

export async function getTickets(
  where: Record<string, any> = {},
  limit = 50,
  skip = 0,
  order?: string[],
): Promise<{ tickets: any[]; total: number }> {
  const filter = JSON.stringify({ where, limit, skip, ...(order ? { order } : {}) });
  return getData(`${BASE}?filter=${encodeURIComponent(filter)}`, AUTH).then((data: any) => data.json());
}

export async function getTicketById(id: string): Promise<any> {
  return getData(`${BASE}/${id}`, AUTH).then((data: any) => data.json());
}

export async function createTicket(body: any): Promise<any> {
  return postData(BASE, {
    ...AUTH,
    body,
    headers: { 'Content-Type': 'application/json' },
  }).then((data: any) => data.json());
}

export async function updateTicket(id: string, body: any): Promise<any> {
  return postData(`${BASE}/${id}`, {
    ...AUTH,
    body,
    usePutMethod: true,
    headers: { 'Content-Type': 'application/json' },
  }).then((data: any) => data.json());
}

export async function addComment(
  id: string,
  body: { body: string; isInternal?: boolean; attachments?: { url: string; name: string; mime: string }[] },
): Promise<any> {
  return postData(`${BASE}/${id}/comments`, {
    ...AUTH,
    body: { ...body, authorType: 'agent' },
    headers: { 'Content-Type': 'application/json' },
  }).then((data: any) => data.json());
}

export async function getCategories(): Promise<string[]> {
  return getData(`${BASE}/categories`, AUTH)
    .then((data: any) => data.json())
    .then((res: any) => (Array.isArray(res) ? res : []));
}

export async function getNearestTickets(
  latitude: number,
  longitude: number,
  where?: Record<string, any>,
  limit = 50,
): Promise<any[]> {
  const res = await postData(`${BASE}/nearest`, {
    ...AUTH,
    body: { latitude, longitude, limit, where },
    headers: { 'Content-Type': 'application/json' },
  }).then((data: any) => data.json()).catch(() => []);
  return Array.isArray(res) ? res : [];
}

export async function getTicketPaymentRequests(ticketId: string): Promise<any[]> {
  const res = await getData(`${BASE}/${ticketId}/payment-requests`, AUTH).then((d: any) => d.json()).catch(() => []);
  return Array.isArray(res) ? res : [];
}

export async function createTicketPaymentRequest(
  ticketId: string,
  body: {amount: number; description: string; dueDate?: string},
): Promise<any> {
  return postData(`${BASE}/${ticketId}/payment-requests`, {
    ...AUTH,
    body,
    headers: {'Content-Type': 'application/json'},
  }).then((d: any) => d.json());
}

export async function cancelTicketPaymentRequest(ticketId: string, paymentId: string): Promise<any> {
  return postData(`${BASE}/${ticketId}/payment-requests/${paymentId}/cancel`, {
    ...AUTH,
    body: {},
    headers: {'Content-Type': 'application/json'},
  }).then((d: any) => d.json());
}

export async function getTicketInventoryUsage(ticketId: string): Promise<any[]> {
  const res = await getData(`${BASE}/${ticketId}/inventory-usage`, AUTH).then((d: any) => d.json()).catch(() => []);
  return Array.isArray(res) ? res : [];
}

export async function recordTicketInventoryUsage(
  ticketId: string,
  body: { inventoryItemId: string; fromInventoryLocationId: string; quantity: number; comments?: string },
): Promise<any> {
  return postData(`${BASE}/${ticketId}/inventory-usage`, {
    ...AUTH,
    body,
    headers: { 'Content-Type': 'application/json' },
  }).then((d: any) => d.json());
}

export async function reverseTicketInventoryUsage(ticketId: string, movementId: string): Promise<any> {
  return postData(`${BASE}/${ticketId}/inventory-usage/${movementId}/reverse`, {
    ...AUTH,
    body: {},
    headers: { 'Content-Type': 'application/json' },
  }).then((d: any) => d.json());
}

export async function getAvailableStockAtLocation(
  ticketId: string,
  locationId: string,
): Promise<{ items: { itemId: string; name: string; available: number }[] }> {
  const res = await getData(
    `${BASE}/${ticketId}/inventory-usage/available-stock?locationId=${encodeURIComponent(locationId)}`,
    AUTH,
  ).then((d: any) => d.json()).catch(() => ({ items: [] }));
  return res ?? { items: [] };
}

export async function getSupportAgents(): Promise<{ farmUserId: string; name: string }[]> {
  return getData('/roles/support/users', AUTH)
    .then((data: any) => data.json())
    .then((res: any) => {
      const list: any[] = Array.isArray(res) ? res : [];
      return list.map(u => ({ farmUserId: u.farmUserId, name: u.username, mobile: u.mobile }));
    });
}

export function parsePriorityOrder(priority: string): number {
  switch (priority) {
    case 'high': return 3;
    case 'normal': return 2;
    case 'low': return 1;
    default: return 0;
  }
}

// Returns unique "district_state" keys (ticket.regionId format) from a user's regionIds array.
// e.g. ["Karanjati_Sakri_Dhule_Maharashtra"] → ["Dhule_Maharashtra"]
export function parseRegionIds(regionIds: string[]): string[] {
  const seen = new Set<string>();
  for (const regionId of regionIds) {
    const parts = regionId.split('_');
    if (parts.length >= 2) {
      const key = `${parts[parts.length - 2]}_${parts[parts.length - 1]}`;
      seen.add(key);
    }
  }
  return Array.from(seen);
}
